import { useEffect, useId, useState } from 'react';
import { useCookieConsent } from '@/context/CookieConsentContext';
import { getStoredTermsConsent, TERMS_CONSENT_EVENT } from '@/lib/termsConsent';

export default function CookieConsentBanner() {
  const titleId = useId();
  const descriptionId = useId();
  const [hasTermsConsent, setHasTermsConsent] = useState(false);
  const {
    consent,
    isReady,
    isBannerOpen,
    acceptTracking,
    declineTracking,
    openBanner,
    closeBanner,
  } = useCookieConsent();

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const syncTermsConsent = () => {
      setHasTermsConsent(getStoredTermsConsent());
    };

    const handleTermsConsentChange = (event) => {
      setHasTermsConsent(Boolean(event.detail?.accepted));
    };

    syncTermsConsent();
    window.addEventListener(TERMS_CONSENT_EVENT, handleTermsConsentChange);

    return () => {
      window.removeEventListener(TERMS_CONSENT_EVENT, handleTermsConsentChange);
    };
  }, []);

  if (!isReady || !hasTermsConsent) return null;

  return (
    <>
      {isBannerOpen ? (
        <div className="fixed inset-x-0 bottom-0 z-[120] px-2 pb-2 sm:px-6 sm:pb-6">
          <div
            role="dialog"
            aria-modal="true"
            aria-live="polite"
            aria-labelledby={titleId}
            aria-describedby={descriptionId}
            className="mx-auto w-full max-w-4xl rounded-[1.25rem] border border-[#e9dcc4] bg-[#fffdf8]/95 p-4 shadow-[0_30px_90px_rgba(17,17,17,0.16)] backdrop-blur-xl sm:rounded-[2rem] sm:p-6"
          >
            <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
              <div className="max-w-2xl">
                <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[#786f64]">
                  Cookie Permission
                </p>
                <h2 id={titleId} className="mt-3 text-2xl font-playfair font-bold text-[#171717]">
                  Choose how Trendy Quest uses cookies
                </h2>
                <p id={descriptionId} className="mt-3 text-sm leading-7 text-[#5e574f] sm:text-[15px]">
                  We use essential storage to keep your cart, wishlist, and checkout working.
                  If you allow analytics cookies, we will also use Meta and TikTok pixels to
                  measure visits and purchases.
                </p>
              </div>

              <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
                {consent ? (
                  <button
                    type="button"
                    onClick={closeBanner}
                    className="inline-flex w-full items-center justify-center rounded-full border border-[#d9cdb7] bg-white px-5 py-3 text-sm font-semibold text-[#1b1b1b] transition hover:border-[#bfa37c] sm:w-auto"
                  >
                    Keep current choice
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={declineTracking}
                  className="inline-flex w-full items-center justify-center rounded-full border border-[#d9cdb7] bg-white px-5 py-3 text-sm font-semibold text-[#1b1b1b] transition hover:border-[#bfa37c] sm:w-auto"
                >
                  Essential only
                </button>
                <button
                  type="button"
                  onClick={acceptTracking}
                  className="inline-flex w-full items-center justify-center rounded-full bg-[#171717] px-6 py-3 text-sm font-semibold text-white transition hover:bg-[#2b2622] sm:w-auto"
                >
                  Allow analytics cookies
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {consent && !isBannerOpen ? (
        <button
          type="button"
          onClick={openBanner}
          className="fixed bottom-3 left-3 z-[110] inline-flex max-w-[calc(100vw-1.5rem)] items-center justify-center rounded-full border border-[#ddcfb7] bg-[#fffdf7]/95 px-3 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#2b2a27] shadow-[0_12px_35px_rgba(17,17,17,0.12)] backdrop-blur-md transition hover:border-[#bea47b] sm:bottom-4 sm:left-4 sm:px-4 sm:tracking-[0.18em]"
        >
          Cookie settings
        </button>
      ) : null}
    </>
  );
}
